import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Box, Link } from "@mui/material";
import teamMembers from "../../data/teamMembers";
import Navbar from "../Navbar";
import Footer from "../Footer";
import Error from "../../pages/Error";
import { colors, fonts, gradients, layout } from "../../theme/tokens";

const credCardSx = {
  background: "#fff",
  border: `1px solid ${colors.lineSoft}`,
  borderRadius: "22px",
  p: "26px 28px",
};

const credLabelSx = {
  fontFamily: fonts.display,
  fontSize: "12px",
  fontWeight: 600,
  letterSpacing: ".16em",
  color: colors.steelBlue,
  mb: "16px",
};

const TeamMember = () => {
  const { id } = useParams();
  const member = teamMembers.find((m) => m.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!member) return <Error />;

  return (
    <Box sx={{ overflowX: "hidden", backgroundColor: "#F5F7FA" }}>
      <Navbar />

      {/* Hero */}
      <Box
        component="section"
        sx={{
          position: "relative",
          overflow: "hidden",
          pt: { xs: "112px", md: "140px" },
          pb: "36px",
          px: { xs: "20px", md: "28px" },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "-120px",
            right: "-100px",
            width: "460px",
            height: "460px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle,rgba(44,108,168,.14),transparent 65%)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{ position: "relative", maxWidth: layout.maxWidth, mx: "auto" }}
        >
          <Link
            href="/about-us"
            underline="none"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "9px",
              fontFamily: fonts.body,
              fontWeight: 600,
              fontSize: "14px",
              color: colors.mutedText,
            }}
          >
            <Box component="span" sx={{ fontSize: "17px" }}>
              ←
            </Box>{" "}
            Back to team
          </Link>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              mt: "26px",
            }}
          >
            <Box
              sx={{ width: "26px", height: "2px", background: colors.amber }}
            />
            <Box
              sx={{
                fontFamily: fonts.display,
                fontSize: "12.5px",
                fontWeight: 600,
                letterSpacing: ".2em",
                color: colors.steelBlue,
              }}
            >
              LEADERSHIP
            </Box>
          </Box>
          <Box
            component="h1"
            sx={{
              fontFamily: fonts.display,
              fontWeight: 700,
              fontSize: "clamp(32px,4.4vw,52px)",
              lineHeight: 1.06,
              letterSpacing: "-.02em",
              color: colors.navyInk,
              mt: "16px",
              mb: 0,
              maxWidth: "760px",
            }}
          >
            Dedicated expertise for your business success
          </Box>
          <Box
            sx={{
              fontFamily: fonts.body,
              fontSize: "17px",
              lineHeight: 1.65,
              color: colors.mutedText,
              maxWidth: "660px",
              mt: "18px",
            }}
          >
            Our professionals bring a wealth of experience and tailored insight
            to meet your unique business challenges, helping you navigate
            complexity and achieve your objectives.
          </Box>
        </Box>
      </Box>

      {/* Body */}
      <Box
        component="section"
        sx={{ pt: "24px", pb: "80px", px: { xs: "20px", md: "28px" } }}
      >
        <Box
          sx={{
            maxWidth: layout.maxWidth,
            mx: "auto",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: ".82fr 1.18fr" },
            gap: "40px",
            alignItems: "start",
          }}
        >
          {/* Left: identity + credentials */}
          <Box
            sx={{
              position: { md: "sticky" },
              top: "96px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            {/* Identity */}
            <Box
              sx={{
                background: gradients.navy,
                borderRadius: "26px",
                p: "34px 32px",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "-50px",
                  right: "-30px",
                  width: "200px",
                  height: "200px",
                  borderRadius: "50%",
                  background:
                    "radial-gradient(circle,rgba(246,166,35,.2),transparent 65%)",
                }}
              />
              <Box
                sx={{
                  position: "relative",
                  width: "96px",
                  height: "96px",
                  borderRadius: "24px",
                  background: "rgba(255,255,255,.06)",
                  border: "1px solid rgba(255,255,255,.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: fonts.display,
                  fontWeight: 700,
                  fontSize: "34px",
                  color: "#fff",
                  overflow: "hidden",
                }}
              >
                {member.initials}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    height: "6px",
                    background: gradients.amber,
                  }}
                />
              </Box>
              <Box
                component="h2"
                sx={{
                  fontFamily: fonts.display,
                  fontWeight: 700,
                  fontSize: "26px",
                  color: "#fff",
                  mt: "22px",
                  mb: 0,
                  position: "relative",
                }}
              >
                {member.name}
              </Box>
              <Box
                sx={{
                  display: "inline-block",
                  fontFamily: fonts.body,
                  fontSize: "12.5px",
                  fontWeight: 600,
                  color: colors.navyInk,
                  background: gradients.amber,
                  p: "5px 14px",
                  borderRadius: "30px",
                  mt: "12px",
                  position: "relative",
                  whiteSpace: "nowrap",
                }}
              >
                {member.role}
              </Box>
            </Box>

            {/* Designations */}
            {member.designations && member.designations.length > 0 && (
              <Box sx={credCardSx}>
                <Box sx={credLabelSx}>DESIGNATIONS</Box>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: "11px" }}
                >
                  {member.designations.map((d) => (
                    <Box
                      key={d}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "11px",
                        fontFamily: fonts.body,
                        fontSize: "14.5px",
                        color: colors.brandNavy,
                        fontWeight: 500,
                        lineHeight: 1.4,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ color: colors.amber, mt: "1px" }}
                      >
                        ◆
                      </Box>
                      {d}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Memberships */}
            {member.memberships && member.memberships.length > 0 && (
              <Box sx={credCardSx}>
                <Box sx={credLabelSx}>MEMBERSHIPS</Box>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: "11px" }}
                >
                  {member.memberships.map((m) => (
                    <Box
                      key={m}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "11px",
                        fontFamily: fonts.body,
                        fontSize: "14px",
                        color: "#3C4D5E",
                        lineHeight: 1.45,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ color: colors.amber, mt: "2px" }}
                      >
                        ◆
                      </Box>
                      {m}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Education */}
            {member.education && member.education.length > 0 && (
              <Box sx={credCardSx}>
                <Box sx={{ ...credLabelSx, mb: "18px" }}>EDUCATION</Box>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: "16px" }}
                >
                  {member.education.map((e, i) => (
                    <Box key={i} sx={{ display: "flex", gap: "13px" }}>
                      <Box
                        sx={{
                          flex: "none",
                          width: "9px",
                          height: "9px",
                          borderRadius: "50%",
                          background: gradients.amber,
                          mt: "5px",
                        }}
                      />
                      <Box>
                        <Box
                          sx={{
                            fontFamily: fonts.body,
                            fontWeight: 600,
                            fontSize: "14.5px",
                            color: colors.brandNavy,
                            lineHeight: 1.35,
                          }}
                        >
                          {e.title}
                        </Box>
                        <Box
                          sx={{
                            fontFamily: fonts.body,
                            fontSize: "13px",
                            color: "#7A8896",
                            mt: "3px",
                          }}
                        >
                          {e.place}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Right: biography */}
          <Box
            sx={{
              background: "#fff",
              border: `1px solid ${colors.lineSoft}`,
              borderRadius: "26px",
              p: { xs: "32px 28px", md: "46px 48px" },
              boxShadow: "0 18px 44px -34px rgba(14,27,42,.5)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Box
                sx={{ width: "26px", height: "2px", background: colors.amber }}
              />
              <Box
                sx={{
                  fontFamily: fonts.display,
                  fontSize: "12.5px",
                  fontWeight: 600,
                  letterSpacing: ".2em",
                  color: colors.steelBlue,
                }}
              >
                BIOGRAPHY
              </Box>
            </Box>
            <Box
              component="h3"
              sx={{
                fontFamily: fonts.display,
                fontWeight: 700,
                fontSize: "28px",
                lineHeight: 1.12,
                letterSpacing: "-.01em",
                color: colors.navyInk,
                mt: "16px",
                mb: 0,
              }}
            >
              A track record built across industry and advisory
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "18px",
                mt: "24px",
              }}
            >
              {member.bio.map((para, i) => (
                <Box
                  key={i}
                  sx={{
                    fontFamily: fonts.body,
                    fontSize: "15.5px",
                    lineHeight: 1.75,
                    color: "#42525F",
                  }}
                >
                  {para}
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                mt: "34px",
                pt: "28px",
                borderTop: "1px solid #EDF0F4",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              <Box
                sx={{
                  fontFamily: fonts.body,
                  fontSize: "14.5px",
                  color: colors.mutedText,
                }}
              >
                Interested in working with {member.name} and the team?
              </Box>
              <Link
                href="/contact-us"
                underline="none"
                sx={{
                  background: gradients.amber,
                  color: "#fff",
                  fontFamily: fonts.body,
                  fontWeight: 700,
                  fontSize: "14px",
                  p: "13px 26px",
                  borderRadius: "42px",
                  boxShadow: "0 12px 28px -14px rgba(242,121,28,.7)",
                }}
              >
                Book a Consultation
              </Link>
            </Box>
          </Box>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
};

export default TeamMember;
